import {Bundle, DisplayMetadata, Image, Variation} from '@sportngin/se-commerce-sdk/models/clare';
import {Product} from '../../../models/clare';
import {CreateBundleRequest, UpdateBundleRequest} from '../../../models/clare/bundle';
import {BundleFormValue} from '../types/create-bundle.types';

function getBundleImages(bundle: Bundle): Image[] {
  if (bundle.display_metadata?.images?.length) {
    return bundle.display_metadata.images;
  }

  if (bundle.image_url) {
    return [{src: bundle.image_url, alt: bundle.name}];
  }

  return [];
}

/**
 * Maps create-bundle form values and context to the bundles API request body.
 */
export function mapToBundleApiRequest(
  values: BundleFormValue,
  catalogIds: number[],
  active: boolean,
  images: Image[] = []
): CreateBundleRequest {
  const priceNum = Number(values.bundlePrice);
  const priceCents = Math.round((Number.isNaN(priceNum) ? 0 : priceNum) * 100);
  const defaultImageAlt = values.imageDescription?.trim() || values.bundleName?.trim() || '';
  const displayImages = images
    .map(image => ({
      src: image.src?.trim(),
      alt: image.alt?.trim() || defaultImageAlt,
    }))
    .filter((image): image is Image => !!image.src);
  const displayMetadata: DisplayMetadata = {
    fulfilled_by: values.fulfilledBy,
    images: displayImages.length ? displayImages : undefined,
  };

  return {
    name: values.bundleName?.trim() ?? '',
    description: values.description?.trim() || undefined,
    price_cents: priceCents,
    active,
    image_url: displayImages[0]?.src || undefined,
    catalog_ids: catalogIds,
    display_metadata: displayMetadata,
  };
}

/**
 * Maps a bundle API response to a Product shape for the bundle detail store.
 * We use Product here so the bundle detail page can reuse the same ProductDetailVm and UI
 * as the product detail page; the shared component expects product shape.
 */
export function bundleResponseToItem(bundle: Bundle): Product {
  const defaultVariationId = bundle.id;
  const defaultVariation: Variation = {
    id: defaultVariationId,
    price_cents: bundle.price_cents,
    active: bundle.active,
  };

  return {
    id: bundle.id,
    name: bundle.name,
    description: bundle.description ?? undefined,
    active: bundle.active,
    only_sold_in_bundles: false,
    catalog_id: bundle.catalog_id,
    default_variation_id: defaultVariationId,
    default_variation: defaultVariation,
    variations: [defaultVariation],
    display_metadata: {
      images: getBundleImages(bundle),
    },
  };
}

/**
 * Builds UpdateBundleRequest from the bundle-detail Product shape for PUT /finance/bundles/:id.
 * Used so bundle detail page uses PUT (full replace) while product detail uses PATCH (partial).
 */
export function itemToUpdateBundleRequest(
  item: Product,
  overrides?: Partial<UpdateBundleRequest>
): UpdateBundleRequest {
  const priceCents = item.default_variation?.price_cents ?? 0;
  const hasImages = Array.isArray(item.display_metadata?.images);
  const images = hasImages
    ? item.display_metadata!.images!.map(image => ({src: image.src, alt: image.alt}))
    : undefined;
  const imageUrl = hasImages ? (images?.[0]?.src ?? null) : undefined;
  const payload: UpdateBundleRequest = {
    name: item.name,
    description: item.description,
    price_cents: priceCents,
    active: item.active,
    image_url: imageUrl,
    display_metadata: hasImages ? {images: images ?? []} : undefined,
  };

  return {...payload, ...overrides};
}
